import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Container, Typography, InputBase, Grid, Card, CardContent,
  Avatar, Button, Chip, CircularProgress, Skeleton, Pagination,
  Alert, keyframes,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveAlt1Icon from "@mui/icons-material/PersonRemoveAlt1";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { Pets } from "@mui/icons-material";
import { membersApi, MemberItem } from "../../services/chatService";
import { followApi } from "../../services/chatService";
import { MEMBERSHIP_COLORS } from "../../components/membership/MembershipStyles";

const BLUE  = MEMBERSHIP_COLORS.blue;
const NAVY  = MEMBERSHIP_COLORS.navy;
const WHITE = MEMBERSHIP_COLORS.white;
const BG    = MEMBERSHIP_COLORS.bg;
const MUTED = MEMBERSHIP_COLORS.muted;
const BORDER = MEMBERSHIP_COLORS.border;

const fadeSlideUp = keyframes`
  from { opacity:0; transform:translateY(24px); }
  to   { opacity:1; transform:translateY(0); }
`;
const floatOrb = keyframes`
  0%,100% { transform:translateY(0) scale(1); }
  50%      { transform:translateY(-18px) scale(1.05); }
`;


const Members: React.FC = () => {
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingFollow, setLoadingFollow] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mounted, setMounted] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSearch(v);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { setDebouncedSearch(v); setPage(1); }, 400);
  };

  const loadMembers = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await membersApi.list(page, debouncedSearch);
      setMembers(result.data);
      setTotalPages(result.pagination.pages);
    } catch {
      setError("Failed to load members. Please try again.");
    } finally { setLoading(false); }
  }, [page, debouncedSearch]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleFollow = async (member: MemberItem) => {
    setLoadingFollow(p => ({ ...p, [member._id]: true }));
    try {
      if (member.isFollowing) {
        await followApi.unfollow(member._id);
        setMembers(prev => prev.map(m => m._id === member._id ? { ...m, isFollowing: false, isMutual: false } : m));
      } else {
        const result = await followApi.follow(member._id);
        setMembers(prev => prev.map(m => m._id === member._id ? { ...m, isFollowing: true, isMutual: result.isMutual } : m));
      }
    } catch { setError("Action failed. Please try again."); }
    finally { setLoadingFollow(p => ({ ...p, [member._id]: false })); }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BG, pb: 10 }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #0D2137 50%, #0A2040 100%)`,
        position: "relative", overflow: "hidden",
        pt: { xs: 6, md: 9 }, pb: { xs: 7, md: 10 },
      }}>
        {/* Grid */}
        <Box sx={{
          position: "absolute", inset: 0, opacity: .04,
          backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }} />

        {/* Orbs */}
        <Box sx={{
          position: "absolute", top: "-60px", right: "-60px",
          width: 340, height: 340, borderRadius: "50%",
          background: "radial-gradient(circle,rgba(21,101,192,0.3) 0%,transparent 70%)",
          animation: `${floatOrb} 8s ease-in-out infinite`,
          pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", bottom: "-40px", left: "8%",
          width: 220, height: 220, borderRadius: "50%",
          background: "radial-gradient(circle,rgba(66,165,245,0.18) 0%,transparent 70%)",
          animation: `${floatOrb} 10s ease-in-out infinite 2s`,
          pointerEvents: "none",
        }} />

        {/* Decorative icons */}
        <Box sx={{ position: "absolute", top: 24, right: { xs: 20, md: 80 }, opacity: .1, pointerEvents: "none" }}>
          <PeopleAltOutlinedIcon sx={{ fontSize: 90, color: WHITE }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 24, right: { xs: 40, md: 180 }, opacity: .07, pointerEvents: "none" }}>
          <Pets sx={{ fontSize: 60, color: WHITE }} />
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(24px)",
            transition: "all .7s cubic-bezier(.22,1,.36,1)",
          }}>
            {/* Badge */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              bgcolor: "rgba(66,165,245,.15)", border: "1px solid rgba(66,165,245,.3)",
              borderRadius: "50px", px: 2.5, py: .8, mb: 2.5,
            }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", bgcolor: "#4CAF50",
                boxShadow: "0 0 8px #4CAF50",
                animation: "gp 1.8s ease-in-out infinite",
                "@keyframes gp": { "0%,100%": { opacity: 1 }, "50%": { opacity: .5 } },
              }} />
              <Typography sx={{ color: "#90CAF9", fontWeight: 700, fontSize: ".8rem", letterSpacing: ".1em" }}>
                Community
              </Typography>
            </Box>

            <Typography sx={{
              fontWeight: 900, color: WHITE, lineHeight: 1.12,
              fontSize: { xs: "2rem", md: "3rem" }, letterSpacing: "-.03em", mb: 1.5,
            }}>
              Connect with{" "}
              <Box component="span" sx={{
                background: "linear-gradient(135deg,#42A5F5,#90CAF9)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                Members
              </Box>
            </Typography>

            <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: "1rem", maxWidth: 480 }}>
              Discover fellow learners — follow each other to unlock private chat and build your network.
            </Typography>
          </Box>
        </Container>

        {/* Bottom wave */}
        <Box sx={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0, zIndex: 2 }}>
          <svg viewBox="0 0 1440 50" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 44 }}>
            <path d="M0,28 C400,52 1040,4 1440,28 L1440,50 L0,50 Z" fill={BG} />
          </svg>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 4 } }}>

        {/* ── Search ───────────────────────────────────────────── */}
        <Box sx={{
          display: "flex", alignItems: "center",
          bgcolor: WHITE,
          border: `1.5px solid ${BORDER}`,
          borderRadius: "16px", px: 2, py: 1.2,
          mb: { xs: 3, md: 4 }, maxWidth: 540, gap: 1.5,
          boxShadow: "0 2px 12px rgba(21,101,192,0.07)",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:focus-within": {
            borderColor: BLUE,
            boxShadow: `0 2px 18px rgba(21,101,192,0.15)`,
          },
        }}>
          <SearchIcon sx={{ color: BLUE, fontSize: 20 }} />
          <InputBase
            placeholder="Search members by name…"
            value={search}
            onChange={handleSearchChange}
            fullWidth
            sx={{ fontSize: 15, color: NAVY }}
          />
          {search && (
            <Typography
              variant="caption"
              sx={{ color: MUTED, cursor: "pointer", px: .5, fontWeight: 600, "&:hover": { color: BLUE } }}
              onClick={() => { setSearch(""); setDebouncedSearch(""); setPage(1); }}
            >
              Clear
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "14px", border: `1px solid rgba(21,101,192,0.2)` }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ── Member grid ───────────────────────────────────────── */}
        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
                  <SkeletonCard />
                </Grid>
              ))
            : members.map((member, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={member._id}>
                  <Box sx={{
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? "translateY(0)" : "translateY(20px)",
                    transition: `all .5s cubic-bezier(.22,1,.36,1) ${i * .05}s`,
                  }}>
                    <MemberCard
                      member={member}
                      followLoading={!!loadingFollow[member._id]}
                      onFollow={() => handleFollow(member)}
                    />
                  </Box>
                </Grid>
              ))}
        </Grid>

        {/* ── Empty state ───────────────────────────────────────── */}
        {!loading && members.length === 0 && (
          <Box sx={{
            textAlign: "center", py: 10,
            bgcolor: WHITE, borderRadius: "24px",
            border: `1px dashed ${BORDER}`, mt: 2,
            animation: `${fadeSlideUp} .5s ease both`,
          }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: "50%",
              background: `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2.5,
            }}>
              <PeopleAltOutlinedIcon sx={{ fontSize: 34, color: WHITE }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: NAVY, mb: .5 }}>
              {debouncedSearch ? "No members found" : "No other members yet"}
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED }}>
              {debouncedSearch ? "Try a different search term." : "Be the first to explore the community!"}
            </Typography>
          </Box>
        )}

        {/* ── Pagination ────────────────────────────────────────── */}
        {!loading && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": { color: MUTED, fontWeight: 600 },
                "& .Mui-selected": { bgcolor: `${BLUE} !important`, color: `${WHITE} !important` },
                "& .MuiPaginationItem-root:hover": { bgcolor: "rgba(21,101,192,0.1)" },
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

// ── Skeleton Card ──────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <Card elevation={0} sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: "20px" }}>
    <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, p: 3, "&:last-child": { pb: 3 } }}>
      <Skeleton variant="circular" width={80} height={80} sx={{ bgcolor: "rgba(21,101,192,0.07)" }} />
      <Skeleton width={110} height={18} sx={{ bgcolor: "rgba(21,101,192,0.07)", borderRadius: 1 }} />
      <Skeleton width={150} height={14} sx={{ bgcolor: "rgba(21,101,192,0.05)", borderRadius: 1 }} />
      <Skeleton width={80} height={22} sx={{ bgcolor: "rgba(21,101,192,0.06)", borderRadius: 4 }} />
      <Skeleton width="100%" height={36} sx={{ bgcolor: "rgba(21,101,192,0.06)", borderRadius: 2 }} />
    </CardContent>
  </Card>
);

// ── Member Card ────────────────────────────────────────────────
interface MemberCardProps {
  member: MemberItem;
  followLoading: boolean;
  onFollow: () => void;
}

const MemberCard: React.FC<MemberCardProps> = ({ member, followLoading, onFollow }) => (
  <Card
    elevation={0}
    sx={{
      bgcolor: WHITE,
      border: member.isMutual ? `1.5px solid rgba(21,101,192,0.4)` : `1px solid ${BORDER}`,
      borderRadius: "20px",
      transition: "box-shadow 0.25s, transform 0.25s, border-color 0.25s",
      height: "100%",
      "&:hover": {
        boxShadow: "0 12px 32px rgba(21,101,192,0.14)",
        transform: "translateY(-4px)",
        borderColor: member.isMutual ? "rgba(21,101,192,0.6)" : "rgba(21,101,192,0.25)",
      },
    }}
  >
    <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, pt: 3.5, px: 2.5, pb: 2.5, "&:last-child": { pb: 2.5 } }}>

      {/* Avatar */}
      <Box sx={{ position: "relative" }}>
        <Avatar
          src={member.avatar}
          alt={member.name}
          sx={{
            width: 80, height: 80,
            background: `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
            fontSize: 30, fontWeight: 800, color: WHITE,
            boxShadow: member.isMutual
              ? `0 0 0 3px rgba(21,101,192,0.25), 0 4px 16px rgba(21,101,192,0.2)`
              : "0 4px 14px rgba(21,101,192,0.15)",
          }}
        >
          {member.name[0]?.toUpperCase()}
        </Avatar>
        {member.isMutual && (
          <Box sx={{
            position: "absolute", bottom: 1, right: 1,
            width: 22, height: 22, borderRadius: "50%",
            bgcolor: WHITE, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: BLUE }} />
          </Box>
        )}
      </Box>

      {/* Name + bio */}
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Typography variant="body1" fontWeight={700} noWrap sx={{ color: NAVY, fontSize: "0.97rem", letterSpacing: "-0.01em" }}>
          {member.name}
        </Typography>
        {member.bio ? (
          <Typography variant="caption" sx={{
            color: MUTED, display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden", mt: .4, lineHeight: 1.45, fontSize: "0.75rem",
          }}>
            {member.bio}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "rgba(120,144,156,0.5)", fontStyle: "italic", mt: .4, fontSize: "0.73rem" }}>
            No bio yet
          </Typography>
        )}
      </Box>

      {/* Status chip */}
      {member.isMutual ? (
        <Chip
          size="small"
          icon={<HowToRegIcon style={{ fontSize: 13, color: BLUE }} />}
          label="Connected"
          sx={{
            fontSize: "0.7rem", height: 24,
            bgcolor: "rgba(21,101,192,0.09)", color: BLUE,
            border: `1px solid rgba(21,101,192,0.25)`, fontWeight: 700,
            "& .MuiChip-icon": { ml: "6px" },
          }}
        />
      ) : member.isFollowedBy && !member.isFollowing ? (
        <Chip
          size="small"
          label="Follows you"
          sx={{
            fontSize: "0.7rem", height: 24,
            bgcolor: "rgba(21,101,192,0.05)", color: MUTED,
            border: `1px solid ${BORDER}`, fontWeight: 600,
          }}
        />
      ) : null}

      {/* Follow button */}
      <Box sx={{ width: "100%", mt: .5 }}>
        <Button
          variant={member.isFollowing ? "outlined" : "contained"}
          size="small"
          fullWidth
          disabled={followLoading}
          onClick={onFollow}
          startIcon={
            followLoading ? <CircularProgress size={13} color="inherit" />
              : member.isFollowing ? <PersonRemoveAlt1Icon sx={{ fontSize: "15px !important" }} />
              : <PersonAddAlt1Icon sx={{ fontSize: "15px !important" }} />
          }
          sx={{
            borderRadius: "12px", fontSize: "0.78rem",
            fontWeight: 700, letterSpacing: "0.01em", textTransform: "none",
            py: .9,
            ...(member.isFollowing
              ? {
                  color: MUTED, borderColor: BORDER,
                  "&:hover": { borderColor: "#c0392b", color: "#c0392b", bgcolor: "rgba(192,57,43,0.04)" },
                }
              : {
                  bgcolor: BLUE, color: WHITE,
                  boxShadow: "0 4px 12px rgba(21,101,192,0.3)",
                  "&:hover": { bgcolor: NAVY, boxShadow: "0 6px 18px rgba(21,101,192,0.35)" },
                }),
          }}
        >
          {member.isFollowing ? "Unfollow" : "Follow"}
        </Button>
      </Box>
    </CardContent>
  </Card>
);

export default Members;
